import React, {useEffect, useState, useMemo, useCallback, useRef} from "react";
import {useLocation} from "react-router-dom";
import {usePostHog} from "posthog-js/react";
import {useTranslation} from "react-i18next";
import {inject, observer} from "mobx-react";
import debounce from "lodash.debounce";
//local
import {Layout} from "components/UI/layout";
import {
  IconBinoculars,
  IconPlusCircle,
  IconFunnel,
  IconCog,
  IconCross,
  IconMailLetter,
} from "components/icons";
import {Tabs} from "components/Tabs";
import {TextBadge} from "components/TextBadge";
import Confetti from "components/Confetti";
import {Info} from "components/Info";
import PublicRfpsTableHead from "./components/PublicRfpsTableHead";
import PublicRfpsTableItem from "./components/PublicRfpsTableItem";
import {DecorativeSearch} from "components/Filters/DecorativeSearch";
import {Preloader} from "components/Preloader";
import {PaginationType} from "components/Pagination";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import MiniSelect from "components/selects/MiniSelect";
import MultipleSelect from "components/selects/MultipleSelect";
import SelectionRow from "components/selects/SelectionRow";
import CustomRadioSwitch from "components/CustomRadioSwitch";
import EmptyData from "components/EmptyData";
import {LockValue} from "components/LockValue";
import {UpsaleBoxLocalModal} from "components/modals/UpsaleBoxLocalModal";
import CustomCampaign from "components/CustomCampaign";
import {PublicRfpFilterSettingsModal} from "components/modals/PublicRfpFilterSettingsModal";
import {sortPublicRFTSelectItems} from "constants/seller.consts";
import {hasPermission, copyToClipboard} from "helpers/helper";
import {publicRfpsFilterRow, publicRFPsTabs} from "constants/tabs.const";
import {color} from "constants/color.consts";
import {ShareButton} from "components/buttons/ShareButton";
import HandyPointerBlob from "components/HandyPointerBlob";
import {defaultClosedAtFieldSearch} from "constants/user.consts";
import {useSavedFilters} from "helpers/userHooks";

const PublicRfpsPage = inject("store")(
  observer(({store: {auth, seller, clientStore}}) => {
    const {loading} = seller;
    const {language} = clientStore;
    const {
      user: {id, firstName, lastName, permissions, email, ActiveOrganization},
    } = auth;
    const {t} = useTranslation();
    const location = useLocation();
    const searchInputRef = useRef("");

    const [filters, setRfpFilters, clearRfpFilters] = useSavedFilters({
      key: "publicRfpsFilter",
      defaultValue: {...defaultClosedAtFieldSearch, tab: publicRFPsTabs[0].requestKey},
      allowedKeys: [
        "field",
        "direction",
        "tab",
        "page",
        "search",
        "availableIn",
        "entrepreneurOffers",
        "entrepreneurWithin",
        "materialSupplierType",
        "consultingServices",
      ],
      allowedValues: {field: ["closedAt", "publishedAt"], tab: ["open", "external"]},
      storage: "local",
    });

    const [publicRfpsData, setPublicRfpsData] = useState(null);
    const [filterMonitoringSettings, setMonitoringFilterSettings] = useState(null);
    const [isPublicPFPFilterSettingsModal, setPublicPFPFilterSettingsModal] =
      useState(false);
    const [saveFilterSettingsLoading, setSaveFilterSettingsLoading] = useState(false);
    const [enableMonitoringToggle, setEnableMonitoringToggle] = useState(false);

    const isFilledFilters = useMemo(() => {
      if (!filters) return false;
      const {page, tab, search, ...params} = filters;
      return Object.keys(params).length > 2;
    }, [filters]);

    const activeTab = useMemo(() => {
      return (
        publicRFPsTabs.find(
          (tab) =>
            tab.requestKey ===
            (filters ?? Object.fromEntries(new URLSearchParams(location.search)))?.tab
        )?.id ?? 0
      );
    }, [location.search, filters]);

    const activeTabKey = useMemo(
      () => publicRFPsTabs.find((tab) => tab.id === activeTab)?.requestKey,
      [activeTab]
    );
    const isOpenTab = useMemo(() => activeTabKey === "open", [activeTabKey]);
    const isExternalTab = useMemo(() => activeTabKey === "external", [activeTabKey]);
    const maxCategories =
      permissions.find(
        (item) => item.PermissionId === "LIMIT_OPEN_RFT_MONITOR_MAX_CATEGORIES"
      )?.quantity ?? 0;
    const maxRegions =
      permissions.find(
        (item) => item.PermissionId === "LIMIT_OPEN_RFT_MONITOR_MAX_REGIONS"
      )?.quantity ?? 0;

    const filterRowItems = useMemo(
      () =>
        Object.entries(filters ?? {})
          .filter((item) => Array.isArray(item[1]))
          .map((item) => item[1].map((value) => [item[0], value]))
          .flat()
          .sort((a, b) => (a[0] > b[0] ? -1 : 1)),
      [filters]
    );
    const tableSortValue = useMemo(
      () => `${filters?.field}|${filters?.direction}`,
      [filters]
    );

    const showInfoAndClick = hasPermission(["READ_OTHER_OPEN_RFT"]);
    const canEnableFilterMonitoringToggle = hasPermission(
      ["ENABLE_SWITCH_ON_OPEN_RFT_MONITORING_TOGGLE"],
      permissions
    );

    const validateSearch = (search) =>
      search && search !== "undefined" && search !== "null" ? search : null;
    const handleSearch = debounce(() => {
      const search = searchInputRef.current?.value?.trim();
      const validatedSearch = validateSearch(search);
      if (validatedSearch) onFilterChange("search", validatedSearch);
      else onFilterChange("search", null);
    }, 800);

    const fetchPublicRequests = useCallback(
      async (noNeedUpdateRequests) => {
        if (!filters) return;
        isOpenTab
          ? await seller.getPublicRequests(filters).then(setPublicRfpsData)
          : await seller.getExternalRequests(filters).then(setPublicRfpsData);
        isOpenTab &&
          (await auth.getPublicRequestsMonitoringSetting().then(({data}) => {
            if (!data) return;
            const {id, createdAt, updatedAt, ...filterData} = data;
            setMonitoringFilterSettings(filterData);
            if (noNeedUpdateRequests) return;
            const {enableMonitoring} = filterData;
            setEnableMonitoringToggle(enableMonitoring);
          }));
      },
      [isOpenTab, auth, seller, filters]
    );

    const onFilterChange = async (name, value) => {
      const params = ["sort", "tab"].includes(name)
        ? {...filters, ...value}
        : {...filters, [name]: value};
      if (!value || (Array.isArray(value) && !value.length)) delete params[name];
      setRfpFilters(name === "page" ? params : {...params, page: 1});
      setPublicRfpsData(null);
    };

    const handleRemoveFilterValue = (value) => {
      const deletedName = filterRowItems.find((item) => item[1] === value)?.[0];
      const deletedValue = filterRowItems
        .filter((item) => item[0] === deletedName && item[1] !== value)
        .map((item) => item[1]);
      deletedName && onFilterChange(deletedName, deletedValue);
    };

    const clearFilters = useCallback(() => {
      clearRfpFilters();
      setPublicRfpsData(null);
      seller.getPublicRequests().then(setPublicRfpsData);
    }, [seller, clearRfpFilters]);

    const handleEnableFilterSettings = useCallback(
      (checked) => {
        if (!canEnableFilterMonitoringToggle) return;
        const enableMonitoring = filterMonitoringSettings?.enableMonitoring;
        checked &&
          !enableMonitoring &&
          !enableMonitoringToggle &&
          setPublicPFPFilterSettingsModal(true);
        setEnableMonitoringToggle(checked);
        if ((!checked && enableMonitoring) || (checked && !enableMonitoring))
          auth
            .setPublicRequestsMonitoringSetting({enableMonitoring: checked})
            .then(({data}) => {
              const {id, createdAt, updatedAt, ...filterData} = data;
              setMonitoringFilterSettings(filterData);
            });
      },
      [
        auth,
        canEnableFilterMonitoringToggle,
        filterMonitoringSettings,
        enableMonitoringToggle,
      ]
    );

    // disabling monitoring in the absence of selected values
    const needChangeMonitoring = useCallback(
      (values) => {
        if (!filterMonitoringSettings) return handleEnableFilterSettings(false);
        const {enableMonitoring, ...filters} = filterMonitoringSettings;
        const selectedFilters = Object.entries(values ?? filters).filter(
          (item) => !!item[1]?.length
        );
        const hasEnoughSelectedFilters =
          selectedFilters.length > 1 &&
          selectedFilters.some((item) => item[0] === "availableIn");
        if (hasEnoughSelectedFilters)
          !enableMonitoring && handleEnableFilterSettings(true);
        else handleEnableFilterSettings(false);
      },
      [filterMonitoringSettings, handleEnableFilterSettings]
    );

    const handleSaveFilterSettings = async (values) => {
      setSaveFilterSettingsLoading(true);
      const payload = Object.entries(values)
        .filter((item) => !!item[1])
        .reduce((filters, item) => Object.assign(filters, {[item[0]]: item[1]}), {});
      await auth
        .setPublicRequestsMonitoringSetting(payload)
        .then(() => {
          needChangeMonitoring(values);
          setPublicPFPFilterSettingsModal(false);
        })
        .finally(() => setSaveFilterSettingsLoading(false));
      await fetchPublicRequests(true);
    };

    const handleClosePublicRfpFilterSettingsModal = () => {
      enableMonitoringToggle && needChangeMonitoring();
      setPublicPFPFilterSettingsModal(false);
    };

    useEffect(() => {
      fetchPublicRequests();
    }, [fetchPublicRequests]);

    const posthog = usePostHog();
    const copyLink = () => {
      posthog?.capture("Clicked share button on public RFT listing page");
      copyToClipboard(window.location, true);
    };

    return (
      <Layout>
        <div className="public-rfps">
          <div className="content-block_header">
            <div className="heading">
              <IconBinoculars />
              <h3 className="content-block_title __uppercase mr-2">{t("PUBLIC_RFP")}</h3>
              <span className="live">{t("MAIN_MENU_BADGE_LIVE")}</span>
              <Info tooltip="PUBLIC_RFPS_PAGE_BETA_INFO_LABEL" centerText />
            </div>

            {!!filters && (
              <DecorativeSearch
                defaultSearchValue={filters?.search}
                searchFocus={searchInputRef}
                searchOnChange={handleSearch}
                searchPlaceholder={t("PUBLIC_RFPS_PAGE_FILTER_SEARCH_PLACEHOLDER")}
                showSearch
                hideFilterParts={false}
                hideEntireFilter={false}
                filterToggle={true}
                isFilterVisible={!!filters?.search}
                showClearIcon
                onClearSearch={() => onFilterChange("search", null)}
                addClass="mb-0 mla"
              />
            )}
            {isOpenTab && (
              <div className="notifyer df-row-center ml-8">
                <h2>{t("PUBLIC_RFPS_PAGE_FILTER_SETTINGS_HEADING")}</h2>

                {!!publicRfpsData && !filterMonitoringSettings?.enableMonitoring && (
                  <React.Fragment>
                    <HandyPointerBlob
                      text="PUBLIC_RFPS_PAGE_FILTER_SETTINGS_DESCRIPTION"
                      absolutePosition
                      showPointer
                      pointerDirection="down"
                      pointerPosition="last"
                      position={{top: "-25px", right: "70px"}}
                    />
                  </React.Fragment>
                )}

                <LockValue
                  canPermissions={["READ_PUBLIC_RFTS"]}
                  needLabel
                  needShowModalToSubscribe
                  addClassIcon="mr-3 ml-0"
                >
                  <ButtonTransparent
                    icon={<IconCog height="16" width="16" />}
                    noBorder
                    small
                    addClass="__rotate-on-hover pa-0"
                    onClick={() =>
                      hasPermission(["READ_PUBLIC_RFTS"]) &&
                      setPublicPFPFilterSettingsModal(true)
                    }
                  />
                </LockValue>

                <LockValue
                  canPermissions={
                    !enableMonitoringToggle
                      ? ["ENABLE_SWITCH_ON_OPEN_RFT_MONITORING_TOGGLE"]
                      : []
                  }
                  needLabel
                  addClassIcon="open-rft-switch-locked-icon"
                  needShowModalToSubscribe
                >
                  <CustomRadioSwitch
                    checked={enableMonitoringToggle}
                    uppercase
                    small
                    isActive={handleEnableFilterSettings}
                  />
                </LockValue>
                <IconMailLetter height={15} width={15} addClass="ml-1 mr-2" />

                <Info
                  addWrapperClass="ml-2"
                  tooltip="PUBLIC_RFPS_PAGE_FILTER_STAT_TOOLTIP"
                  centerText
                />
              </div>
            )}
            <ShareButton
              addClass="share-feature ml-8"
              name="SHARE_THIS"
              onClickCopy={copyLink}
            />
          </div>

          <div className="public-rfps__filters pt-4 pl-8 pr-8 pb-8">
            <div className="df-row-start">
              <IconFunnel height="30" width="30" color={color.green} addClass="mr-4" />
              <div className="df-row-wrap-center">
                <LockValue
                  canPermissions={["READ_OTHER_OPEN_RFT"]}
                  needLabel
                  smallLockIcon={true}
                  needShowModalToSubscribe
                  addClassIcon={"mb-2"}
                >
                  {publicRfpsFilterRow.map((selection) => (
                    <MultipleSelect
                      key={selection.label}
                      value={filters?.[selection.label]}
                      list={selection.array}
                      customSelectAllElement={selection.selectAll}
                      tiny
                      addClass="mr-2 mb-2"
                      uppercaseButton
                      notShowingSelections
                      needPermissions={["READ_OTHER_OPEN_RFT"]}
                      needSearch={selection.isNeedSearch}
                      color={selection.color}
                      bottomElement={selection.bottomElement}
                      onChange={(value) => onFilterChange(selection.label, value)}
                      title={selection.title}
                      titleInButton
                    />
                  ))}
                </LockValue>
              </div>

              {isFilledFilters && !!publicRfpsData && (
                <div className="df-row-wrap-jce-start mla">
                  <IconFunnel addClass="mr-1 mb-2" />
                  <span>
                    {t("PUBLIC_RFPS_PAGE_FILTER_STAT")?.format(
                      publicRfpsData.totalItems,
                      publicRfpsData.totalUnfilteredItems
                    )}
                  </span>
                  <ButtonTransparent
                    name={t("CLEAR_FILTERS_BUTTON")}
                    onClick={clearFilters}
                    icon={<IconCross color={color.link} width="7" height="7" />}
                    addClass="__uppercase ml-4"
                    tiny
                  />
                </div>
              )}
            </div>
            {isFilledFilters && (
              <SelectionRow
                values={filterRowItems.map((item) => ({
                  value: item[1],
                  label: item[1],
                  color: item[1].includes("REGION") ? "blue" : "green",
                }))}
                tiny
                addClass="ml-10"
                onRemove={handleRemoveFilterValue}
              />
            )}
            <MiniSelect
              key={tableSortValue}
              list={sortPublicRFTSelectItems}
              defaultValue={tableSortValue}
              prefixLabel
              boldLabel
              tinyLabel
              uppercaseLabel
              itemAsObject
              addClass="ml-10"
              onChange={(value) =>
                onFilterChange("sort", {
                  field: value.split("|")[0],
                  direction: value.split("|")[1],
                })
              }
              label={t("PUBLIC_RFT_SORT_LABEL")}
            />
          </div>

          <Tabs
            tabs={publicRFPsTabs}
            active={activeTab}
            setActive={(tab) => {
              const value = publicRFPsTabs.find((t) => t.id === tab).requestKey;
              searchInputRef.current.value = "";
              onFilterChange("tab", {tab: value, search: null, page: 1});
            }}
            addClass="pos-a bottom-4 right-8"
            //TODO Beta tag and fancy effect on public tabs
            rightContent={{
              PUBLIC_RFPS_PAGE_EXTERNAL_TAB: (
                <React.Fragment>
                  <TextBadge
                    text={t("OPEN_RFTS_PUBLIC_BETA_TAG")}
                    color="red"
                    addClass="ml-[-20]"
                  />
                  <Confetti
                    drip
                    moveDripX={-80}
                    moveDripY={10}
                    limitOncePerSession
                    sessionKey="public-rfts-drip"
                  />
                </React.Fragment>
              ),
            }}
          />
        </div>
        {loading && <Preloader addClass="preloader500" />}
        {!loading && (
          <div className="public-rfps content-block_wrapper">
            <UpsaleBoxLocalModal
              permissions={["READ_OTHER_OPEN_RFT"]}
              data={{
                title: "UPGRADE_TO_GET_SUBSCRIPTION_06",
                buttonServiceName: "SUBSCRIPTION_NAME_06",
                buttonIcon: <IconPlusCircle />,
                priceLabel: "SUBSCRIPTION_TAB_PRICE_FROM",
                priceValue: "CURRENT_PRICE_SUBSCRIPTION_06",
                priceType: "PRICE_MESSAGE_OFFER_SUB_06",
              }}
              extraData={{
                title: "OPEN_RFTS_UPSALE_BLOG_HEADING_HOW_IT_WORKS",
                bookButtonUrl: "OPEN_RFTS_UPSALE_BOOK_DEMO_URL",
                infoButtonUrl: "OPEN_RFTS_UPSALE_MORE_INFO_URL",
                videoTitle: "Nöjd kund berättar - Interoc",
                videoUrl: "OPEN_RFTS_UPSALE_VIDEO_URL",
                videoSize: {width: "233", height: "142"},
              }}
              addClass="public-rfps-positioning"
            />

            {/* This is temporary info blob about public procurements BETA */}
            {isExternalTab && (
              <div className="ml-2 mb-2 mt-[-10]">
                <span className="__italic fz-16 __normal">
                  {t("TEMP_PUBLIC_PROCUREMENTS_BETA_TEXT")}
                </span>
                <span className="fz-20">
                  {" "}
                  {t("TEMP_PUBLIC_PROCUREMENTS_BETA_EMOJIS")}
                </span>
              </div>
            )}

            {/* 
                This is a timed campaign widget with a countdown.
                It sits fixed as a bottom strip on the screen on the public RFPs page.
                Optionally, we can add a custom page effect (currently)
                It all disappears when the countdown (endDate) is over.
                */}
            <CustomCampaign
              show={!showInfoAndClick}
              endDate="2025-12-31T23:59:59"
              tooltipTextKey="CUSTOM_CAMPAIGN_TOOLTIP"
              textKey="CUSTOM_CAMPAIGN_TEXT"
              detailsKey="CUSTOM_CAMPAIGN_DETAILS"
              backgroundColor={color.red}
              textColor="white"
              pageEffect="snowfall"
            />
            <PublicRfpsTableHead isExternal={isExternalTab} />
            {publicRfpsData?.requests?.length ? (
              publicRfpsData.requests.map((publicRfp) => (
                <PublicRfpsTableItem
                  key={publicRfp.id}
                  publicRfp={publicRfp}
                  language={language}
                  currentUser={{id, firstName, lastName}}
                  showInfoAndClick={showInfoAndClick}
                  isExternal={isExternalTab}
                  organizationTurnover={Math.round(
                    ActiveOrganization?.Company?.turnover / 1000
                  )}
                />
              ))
            ) : (
              <EmptyData addClass={`empty-data pt-4`} />
            )}
            <PaginationType
              totalItems={publicRfpsData?.totalItems}
              currentPage={publicRfpsData?.currentPage ?? 1}
              onPageChange={(value) => onFilterChange("page", `${value}`)}
              addClass="mt-8"
            />
          </div>
        )}

        {isPublicPFPFilterSettingsModal && (
          <PublicRfpFilterSettingsModal
            initialFilterSettings={() => {
              if (!filterMonitoringSettings) return {};
              const {enableMonitoring, ...rest} = filterMonitoringSettings;
              return rest;
            }}
            saveLoading={saveFilterSettingsLoading}
            onSave={handleSaveFilterSettings}
            maxCategories={maxCategories}
            maxRegions={maxRegions}
            email={email}
            onClose={handleClosePublicRfpFilterSettingsModal}
          />
        )}
      </Layout>
    );
  })
);

export default PublicRfpsPage;
